import { isDesktopTargetSelectorEnabled } from '@/features/flags/desktop-target-selector-flag';
import { isTauri } from '@/tauri/utils';
import type { QueryClient } from '@tanstack/react-query';

import {
  resolveDesktopTargetSelection,
  targetIdForTurn,
  type DesktopTargetSticky,
} from './desktopTargetSelection';
import {
  DESKTOP_TARGETS_QUERY_KEY,
  desktopTargetsClient,
  type DesktopTarget,
} from './desktopTargets';
import { readNativeInstanceId } from './nativeInstanceId';
import { readDesktopTargetRosterState } from './useDesktopTargetRoster';

const DESKTOP_TARGETS_STALE_TIME_MS = 30_000;

export type ResolveDesktopTargetTurnResult =
  | { status: 'omit' }
  | { status: 'send'; targetId: string };

const fetchDesktopTargetRoster = (queryClient: QueryClient) =>
  queryClient.fetchQuery({
    queryKey: DESKTOP_TARGETS_QUERY_KEY,
    queryFn: async () => (await desktopTargetsClient.list()).data,
    staleTime: DESKTOP_TARGETS_STALE_TIME_MS,
  });

export const fetchCurrentDesktopTarget = async (
  queryClient: QueryClient
): Promise<DesktopTarget | undefined> => {
  if (!isTauri()) return undefined;
  const instanceId = await readNativeInstanceId();
  if (!instanceId) return undefined;
  const roster = await fetchDesktopTargetRoster(queryClient);
  const resolved = resolveDesktopTargetSelection({
    sticky: null,
    roster,
    instanceId,
  });
  return resolved.kind === 'target' ? resolved.target : undefined;
};

export const resolveDesktopTargetTurn = async (
  queryClient: QueryClient,
  {
    desktopTargetEnabled,
    selectorEnabled = isDesktopTargetSelectorEnabled(),
    override = null,
  }: {
    desktopTargetEnabled: boolean;
    selectorEnabled?: boolean;
    override?: DesktopTargetSticky;
  }
): Promise<ResolveDesktopTargetTurnResult> => {
  if (!desktopTargetEnabled) return { status: 'omit' };

  if (selectorEnabled) {
    const cached = readDesktopTargetRosterState(queryClient);
    // Joins the picker's in-flight load; a failure sends the turn without a computer.
    const roster = cached.rosterReady
      ? cached.roster
      : await fetchDesktopTargetRoster(queryClient).catch(() => undefined);
    const instanceId =
      override === null ? await readNativeInstanceId().catch(() => null) : null;
    const targetId = targetIdForTurn(
      resolveDesktopTargetSelection({
        sticky: override,
        roster,
        instanceId,
      })
    );
    return targetId ? { status: 'send', targetId } : { status: 'omit' };
  }

  const target = await fetchCurrentDesktopTarget(queryClient).catch(() => undefined);
  return target ? { status: 'send', targetId: target.target_id } : { status: 'omit' };
};
